import React, { useState, useCallback, useEffect } from 'react';
import SelectField from '../components/SelectField';
import InfoCard from '../components/InfoCard';
import { FaPlay, FaTimes, FaPlus, FaMinus, FaUpload, FaCloud, FaCloudUploadAlt, FaEye, FaFilter, FaCheck, FaCheckCircle, FaTimesCircle, FaRegClock } from 'react-icons/fa';
import { InlineSpinner } from '../components/LoadingState';
import LoadingState from '../components/LoadingState';
import { useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import { uploadRecordingS3, listCampaignsApi, fetchCampaign, triggerbulk, createJobMetadata, fetchInteractionCalls, previewInteractionCalls, singleGenesysUpload, fetchGenesysFilters } from '../api/api';
import GenesysPreviewModal from '../components/GenesysPreviewModal';
import DateTimeInput from '../components/DateTimeInput';

// Queue name to ID mapping (hardcoded for now)
const QUEUE_MAPPINGS = {
  'helpdesk': '',
  // Add more queue mappings here as needed
};

// Format a Date as 'YYYY-MM-DDTHH:mm' in LOCAL time, matching what a
// datetime-local input expects (toISOString() would shift to UTC).
function toLocalDateTimeInputValue(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function detectMeta(filename) {
  const agentMatch = filename.match(/_AGENT_(.*?)_/i);
  const guidMatch  = filename.match(/_GUID_(.*?)_/i);
  const dtMatch    = filename.match(/_DT_([\dT-]+)/i);
  return {
    agent:            agentMatch ? agentMatch[1] : '',
    guid:             guidMatch  ? guidMatch[1]  : '',
    datetime:         dtMatch    ? dtMatch[1].replace(/(\d{4}-\d{2}-\d{2}T\d{2})-(\d{2})/, '$1:$2') : '',
    agentDetected:    !!agentMatch,
    guidDetected:     !!guidMatch,
    datetimeDetected: !!dtMatch,
  };
}

function prepareUploadFile(file, campaignId, templateName, username, jobId, meta = {}) {
  const sanitize = (val) => (val || '').trim().replace(/\s+/g, '-').replace(/_/g, '');
  const ext = file.name.substring(file.name.lastIndexOf('.'));

  const agent = sanitize(meta.agent);
  const guid  = sanitize(meta.guid);
  const hasAnyMeta = agent || guid || (meta.datetime && meta.datetime.trim());

  let newFilename;

  if (hasAnyMeta) {
    // Metadata provided — build filename from tokens only, drop original name
    newFilename = `CUST_${campaignId}_TEMP_${templateName}_ASSIGN_${username}_JOB_${jobId}`;
    if (guid)  newFilename += `_GUID_${guid}`;
    if (agent) newFilename += `_AGENT_${agent}`;
    if (meta.datetime && meta.datetime.trim()) {
      // "2025-01-15T09:30" → "2025-01-15T09-30-00"
      const dt = meta.datetime.trim().replace(/:/g, '-')
        + (meta.datetime.trim().length === 16 ? '-00' : '');
      newFilename += `_DT_${dt}`;
    }
    newFilename += ext;
  } else {
    // No metadata — preserve original filename with prefix
    const lastDotIndex = file.name.lastIndexOf('.');
    const nameWithoutExt = file.name.substring(0, lastDotIndex).replace(/\./g, '');
    const finalName = nameWithoutExt + '_' + ext;
    newFilename = `CUST_${campaignId}_TEMP_${templateName}_ASSIGN_${username}_JOB_${jobId}_${finalName}`;
  }

  return new File([file], newFilename, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

const UploadRecordings = ({ setAlert,username }) => {
  const navigate = useNavigate();
  const { mutate } = useSWRConfig();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [campaignError, setCampaignError] = useState(null);
  const [scorecards, setScorecards] = useState([]);
  const [selectedScorecard, setSelectedScorecard] = useState(null);
  const [loadingScorecards, setLoadingScorecards] = useState(false);
  const [scorecardError, setScorecardError] = useState(null);
  const [convoId, setConvoId] = useState('')         // current text in the conversation-ID input
  const [convoIds, setConvoIds] = useState([])       // confirmed conversation IDs (chips)

  // Genesys fetch mode states
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'genesys'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [fetchingCalls, setFetchingCalls] = useState(false);

  // Preview modal states
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const [fetchingFilters, setFetchingFilters] = useState(false);
  const [agentOptions, setAgentOptions] = useState([]);
  const [wrapUpCodeOptions, setWrapUpCodeOptions] = useState([]);

  const [agentSearch, setAgentSearch] = useState('');
  const [wrapupSearch, setWrapupSearch] = useState('');
  const [isAgentOpen, setIsAgentOpen] = useState(false);
  const [isWrapupOpen, setIsWrapupOpen] = useState(false);

  const [queueOptions, setQueueOptions] = useState([]);
  const [queueSearch, setQueueSearch] = useState('');
  const [isQueueOpen, setIsQueueOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Filter lists based on search input
  const filteredAgents = agentOptions.filter(agent => 
    agent.name.toLowerCase().includes(agentSearch.toLowerCase())
  );

  const filteredWrapupCodes = wrapUpCodeOptions.filter(code => 
    code.name.toLowerCase().includes(wrapupSearch.toLowerCase())
  );

  // Filter states
  const [filters, setFilters] = useState({
    queueIds: [],
    direction: '',
    userIds: [],
    wrapUpCodes: [],
    ani: '',
    dnis: '',
    divisionIds: []
  });

  // Per-agent sampling states
  const [callsPerAgent, setCallsPerAgent] = useState('');
  const [samplingScope, setSamplingScope] = useState('range'); // 'range' or 'day'

  // Filter input helpers
  const [, setUserIdInput] = useState('');
  const [, setWrapUpCodeInput] = useState('');
  const [divisionIdInput, setDivisionIdInput] = useState('');

  // Per-type: the filters endpoint fetches queues/agents/wrap-up codes
  // independently and can return some data with other types failing (e.g. a
  // Genesys permission granted for queues but not wrap-up codes).
  const [genesysFilterErrors, setGenesysFilterErrors] = useState({});

  useEffect(() => {
    const fetchCampaigns = async () => {
      setLoadingCampaigns(true);
      try {
        const res = await listCampaignsApi();
        const items = res.campaigns || [];
        const options = items.map((c) => ({
          label: c.campaignName,
          value: c.campaignName,
        }));
        setCampaigns(options);
      } catch (error) {
        console.error('Error fetching campaigns:', error);
        setCampaignError('Failed to load campaigns.');
      } finally {
        setLoadingCampaigns(false);
      }
    };
    fetchCampaigns();
  }, []);
  useEffect(() => {
    if (!selectedCampaign) {
      setScorecards([]);
      setSelectedScorecard(null);
      return;
    }

    const fetchScorecards = async () => {
      setLoadingScorecards(true);
      setScorecardError(null);
      try {
        const res = await fetchCampaign(selectedCampaign.value);
        const names = res.templates || [];
        setScorecards(names.map((n) => ({ label: n, value: n })));
      } catch (err) {
        console.error('Error loading scorecards:', err);
        setScorecardError('Failed to load scorecards.');
      } finally {
        setLoadingScorecards(false);
      }
    };

    fetchScorecards();
  }, [selectedCampaign]);

useEffect(() => {
  const loadFilters = async () => {
    setFetchingFilters(true);
    try {
      const res = await fetchGenesysFilters('all');
      if (res.success) {
        setQueueOptions(res.data.queues || []);
        setAgentOptions(res.data.agents || []);
        setWrapUpCodeOptions(
          (res.data.wrapUpCodes || []).map((item) => ({
            id: item.id || item.wrapupcodesId,
            name: item.name || item.Name,
          }))
        );
        setGenesysFilterErrors(res.errors || {});
      }
    } catch (error) {
      // The request itself failed (network, auth) rather than a specific
      // filter type — nothing came back, so all three pickers stay empty.
      console.error('Error loading Genesys filters:', error);
      setGenesysFilterErrors({
        queues: 'Could not load live Genesys data.',
        agents: 'Could not load live Genesys data.',
        wrapUpCodes: 'Could not load live Genesys data.',
      });
    } finally {
      setFetchingFilters(false);
    }
  };
  loadFilters();
}, []);

  // Handle file drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const audioFiles = droppedFiles.filter(file =>
      file.type.startsWith('audio/') ||
      file.name.toLowerCase().endsWith('.wav') ||
      file.name.toLowerCase().endsWith('.mp3') ||
      file.name.toLowerCase().endsWith('.m4a') ||
      file.name.toLowerCase().endsWith('.flac')
    );

    // Filter out files with names longer than 200 characters
    const validFiles = audioFiles.filter(file => file.name.length <= 200);
    const rejectedFileCount = audioFiles.length - validFiles.length;
    const nonAudioCount = droppedFiles.length - audioFiles.length;

    if (nonAudioCount > 0 || rejectedFileCount > 0) {
      const messages = [];
      if (nonAudioCount > 0) {
        messages.push(`${nonAudioCount} non-audio file(s) skipped`);
      }
      if (rejectedFileCount > 0) {
        messages.push(`${rejectedFileCount} file(s) with names longer than 200 characters rejected`);
      }
      setAlert({
        variant: 'warning',
        heading: 'Some Files Rejected',
        text: messages.join('. ') + '.'
      });
    }

    setFiles(prev => [...prev, ...validFiles.map(file => ({
      file,
      id: Date.now() + Math.random(),
      status: 'pending',
      progress: 0,
      meta: detectMeta(file.name),
    }))]);
  }, [setAlert]);

  // Handle file input change
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    // Filter out files with names longer than 200 characters
    const validFiles = selectedFiles.filter(file => file.name.length <= 200);
    const rejectedFileCount = selectedFiles.length - validFiles.length;

    if (rejectedFileCount > 0) {
      setAlert({
        variant: 'warning',
        heading: 'Files Rejected',
        text: `${rejectedFileCount} file(s) with names longer than 200 characters were rejected. Please rename and try again.`
      });
    }

    setFiles(prev => [...prev, ...validFiles.map(file => ({
      file,
      id: Date.now() + Math.random(),
      status: 'pending',
      progress: 0,
      meta: detectMeta(file.name),
    }))]);
  };

  const updateFileMeta = (id, field, value) => {
    setFiles(prev => prev.map(f =>
      f.id === id ? { ...f, meta: { ...f.meta, [field]: value } } : f
    ));
  };

  // Remove file
  const removeFile = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  // Upload files
  const handleUpload = async () => {
    if (files.length === 0) {
      setAlert({
        variant: 'warning',
        heading: 'No Files',
        text: 'Please select files to upload.'
      });
      return;
    }

    if (!selectedCampaign) {
      setAlert({
        variant: 'warning',
        heading: 'No Campaign Selected',
        text: 'Please choose a campaign before uploading.'
      });
      return;
    }

    if (!selectedScorecard) {
      setAlert({
        variant: 'warning',
        heading: 'No Scorecard Selected',
        text: 'Please select a scorecard before uploading.'
      });
      return;
    }

    setUploading(true);

    const uploadType = files.length > 5 ? 'bulk' : 'single';
    // Generate unique jobId for this upload session
    const jobId = `job${Date.now()}${Math.random().toString(36).substring(2, 9)}`;

    // Prepare every file once, up front. The renamed files are needed three
    // times — the length check below, the `calls` payload that creates the
    // tracker rows, and the upload loop — and re-deriving them each time risks
    // the three disagreeing.
    const preparedFiles = files.map((fileData) => ({
      fileData,
      uploadFile: prepareUploadFile(fileData.file, selectedCampaign.value, selectedScorecard.value, username, jobId, fileData.meta || {}),
    }));

    // Validate that constructed filenames don't exceed 200 characters
    // (only relevant for files without metadata, since metadata-only names are always short)
    const tooLongFiles = preparedFiles.filter(({ uploadFile }) => uploadFile.name.length > 200);

    if (tooLongFiles.length > 0) {
      setAlert({
        variant: 'error',
        heading: 'Filename Too Long',
        text: `${tooLongFiles.length} file name(s) exceed 200 characters. Please shorten the file name or add metadata to replace it.`,
      });
      setUploading(false);
      return;
    }

    try {
      // Create job metadata before uploading files. Passing `calls` is what
      // creates the tracker#<filename> rows the pipeline then advances and
      // deletes — without it the job's "Files In Progress" view stays empty.
      await createJobMetadata(
        jobId,
        selectedCampaign.value,
        selectedScorecard.value,
        username,
        files.length,
        preparedFiles.map(({ uploadFile }) => ({ filename: uploadFile.name }))
      );

      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < preparedFiles.length; i++) {
        const { fileData, uploadFile } = preparedFiles[i];

        // Update status to uploading
        setFiles(prev => prev.map(f =>
          f.id === fileData.id
            ? { ...f, status: 'uploading', progress: 0 }
            : f
        ));

        try {
          // Always use S3 presigned URL uploads to avoid payload limits
          await uploadRecordingS3(
            uploadFile,
            { campaign: selectedCampaign.value, scorecard: selectedScorecard.value },
            (percent) => {
              setFiles(prev => prev.map(f =>
                f.id === fileData.id ? { ...f, progress: percent } : f
              ));
            },
            uploadType
          );
          

          // Update status to completed
          setFiles(prev => prev.map(f =>
            f.id === fileData.id
              ? { ...f, status: 'completed', progress: 100 }
              : f
          ));
          successCount++;
        } catch (error) {
          setFiles(prev => prev.map(f =>
            f.id === fileData.id
              ? { ...f, status: 'error', progress: 0 }
              : f
          ));
          errorCount++;
          console.error(`Error uploading ${fileData.file.name}:`, error);
        }
      }
      if (uploadType === 'bulk'){
        await triggerbulk()
      }

      if (errorCount === 0) {
        setAlert({
          variant: 'success',
          heading: 'Upload Complete',
          text: `Successfully uploaded ${successCount} ${successCount === 1 ? 'file' : 'files'}.`
        });
      } else {
        setAlert({
          variant: 'warning',
          heading: 'Upload Partially Complete',
          text: `${successCount} files uploaded successfully, ${errorCount} failed.`
        });
      }

      // Refresh call list and navigate to dashboard
      await mutate('calls-list');
      sessionStorage.removeItem('calls_allData');
      sessionStorage.removeItem('calls_nextToken');
      navigate('/');

    } catch (error) {
      console.error('Upload error:', error);
      setAlert({
        variant: 'error',
        heading: 'Upload Failed',
        text: 'Failed to upload files. Please try again.'
      });
    } finally {
      setUploading(false);
    }
  };

  // Helper functions for filter management
  const addToArrayFilter = (filterKey, value, inputSetter) => {
    if (value.trim()) {
      setFilters(prev => ({
        ...prev,
        [filterKey]: [...prev[filterKey], value.trim()]
      }));
      inputSetter('');
    }
  };

  const removeFromArrayFilter = (filterKey, index) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: prev[filterKey].filter((_, i) => i !== index)
    }));
  };

  const updateFilter = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      queueIds: [],
      direction: '',
      userIds: [],
      wrapUpCodes: [],
      ani: '',
      dnis: '',
      divisionIds: []
    });
  };

  // Show preview of how many conversations will be fetched
  const handleShowPreview = async () => {
    if (!selectedCampaign) {
      setAlert({
        variant: 'warning',
        heading: 'No Queue Selected',
        text: 'Please choose a queue before fetching calls.'
      });
      return;
    }

    if (!selectedScorecard) {
      setAlert({
        variant: 'warning',
        heading: 'No Scorecard Selected',
        text: 'Please select a scorecard before fetching calls.'
      });
      return;
    }

    // If no date range is set, default to last 7 days
    let effectiveStartDate = startDate;
    let effectiveEndDate = endDate;

    if (!effectiveStartDate || !effectiveEndDate) {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);
      effectiveStartDate = toLocalDateTimeInputValue(start);
      effectiveEndDate = toLocalDateTimeInputValue(end);

      // Update state for display
      setStartDate(effectiveStartDate);
      setEndDate(effectiveEndDate);
    }

    // Validate date range
    const start = new Date(effectiveStartDate);
    const end = new Date(effectiveEndDate);
    const now = new Date();
    const diffInDays = (end - start) / (1000 * 60 * 60 * 24);

    // End date cannot be in the future
    if (end > now) {
      setAlert({
        variant: 'warning',
        heading: 'Please Check End Date',
        text: 'End date cannot be in the future.'
      });
      return;
    }

    // Date range must be at most 7 days
    if (diffInDays > 7) {
      setAlert({
        variant: 'warning',
        heading: 'Please Adjust Date Range',
        text: `Date range must be 7 days or less. Current range: ${diffInDays.toFixed(1)} days.`
      });
      return;
    }

    // Start date must be before end date
    if (diffInDays < 0) {
      setAlert({
        variant: 'warning',
        heading: 'Please Check Date Range',
        text: 'Start date must be before end date.'
      });
      return;
    }

    // Build filters object
    const activeFilters = {};
    if (filters.queueIds.length > 0) activeFilters.queueIds = filters.queueIds;
    if (filters.direction) activeFilters.direction = filters.direction;
    if (filters.userIds.length > 0) activeFilters.userIds = filters.userIds;
    if (filters.wrapUpCodes.length > 0) activeFilters.wrapUpCodes = filters.wrapUpCodes;
    if (filters.ani) activeFilters.ani = filters.ani;
    if (filters.dnis) activeFilters.dnis = filters.dnis;
    if (filters.divisionIds.length > 0) activeFilters.divisionIds = filters.divisionIds;

    // Show modal and start preview
    setShowPreviewModal(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewData(null);

    try {
      // Call preview API
      const response = await previewInteractionCalls(
        selectedCampaign.value,
        selectedScorecard.value,
        effectiveStartDate,
        effectiveEndDate,
        Object.keys(activeFilters).length > 0 ? activeFilters : null
      );

      if (response.success && response.data) {
        setPreviewData(response.data);
      } else {
        setPreviewError(response.message || 'Failed to get preview');
      }
    } catch (error) {
      console.error('Preview error:', error);
      let errorMessage = 'Failed to get preview. Please try again.';

      if (error.message.includes('404')) {
        errorMessage = 'No conversations found matching the specified criteria.';
      } else if (error.message.includes('400')) {
        errorMessage = error.message;
      }

      setPreviewError(errorMessage);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Fetch calls from Genesys with filters (called after preview confirmation)
  const handleFetchInteractionCalls = async (prevData) => {
    // Close preview modal
    setShowPreviewModal(false);

    // If no date range is set, default to last 7 days
    let effectiveStartDate = startDate;
    let effectiveEndDate = endDate;

    if (!effectiveStartDate || !effectiveEndDate) {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);
      effectiveStartDate = toLocalDateTimeInputValue(start);
      effectiveEndDate = toLocalDateTimeInputValue(end);
    }

    setFetchingCalls(true);

    try {
      const now = new Date();
      const readableDate = now.toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const jobIdForMetadata = `daterange-${readableDate}`;

      // Create job metadata first
      await createJobMetadata(
        jobIdForMetadata,
        selectedCampaign.value,
        selectedScorecard.value,
        username,
        prevData.recordingCount || 0 // totalCalls will be incremented as files are uploaded
      );

      // Build filters object, excluding empty values
      const activeFilters = {};
      if (filters.queueIds.length > 0) activeFilters.queueIds = filters.queueIds;
      if (filters.direction) activeFilters.direction = filters.direction;
      if (filters.userIds.length > 0) activeFilters.userIds = filters.userIds;
      if (filters.wrapUpCodes.length > 0) activeFilters.wrapUpCodes = filters.wrapUpCodes;
      if (filters.ani) activeFilters.ani = filters.ani;
      if (filters.dnis) activeFilters.dnis = filters.dnis;
      if (filters.divisionIds.length > 0) activeFilters.divisionIds = filters.divisionIds;

      // Build per-agent sampling options if a count was entered
      const sampling = callsPerAgent
        ? { callsPerAgent: parseInt(callsPerAgent, 10), samplingScope }
        : null;

      // Fetch the calls from Genesys with filters
      const response = await fetchInteractionCalls(
        selectedCampaign.value,
        selectedScorecard.value,
        effectiveStartDate,
        effectiveEndDate,
        Object.keys(activeFilters).length > 0 ? activeFilters : null,
        jobIdForMetadata,
        sampling
      );

      if (response.success) {
        const filterInfo = Object.keys(activeFilters).length > 0
          ? ` with ${Object.keys(activeFilters).length} filter(s) applied`
          : '';

        setAlert({
          variant: 'success',
          heading: 'Fetch Started',
          text: `Batch job started${filterInfo}. Expected recordings: ${response.data.expectedRecordings}. Processing in background.`
        });

        // Clear form
        setStartDate('');
        setEndDate('');
        setCallsPerAgent('');
        setSamplingScope('range');
        clearAllFilters();

        // Navigate to jobs page
        setTimeout(() => {
          navigate(`/jobs/${jobIdForMetadata}`);
        }, 2000);
      } else {
        throw new Error(response.message || 'Failed to fetch calls');
      }

    } catch (error) {
      console.error('Fetch calls error:', error);

      let errorMessage = 'Failed to fetch calls. Please try again.';

      if (error.message.includes('404')) {
        errorMessage = 'No recordings found matching the specified criteria.';
      } else if (error.message.includes('400')) {
        errorMessage = error.message;
      }

      setAlert({
        variant: 'error',
        heading: 'Fetch Failed',
        text: errorMessage
      });
    } finally {
      setFetchingCalls(false);
    }
  };

  // --- Single (multi-ID) Genesys upload helpers ---

  // Add one or more conversation IDs (from typing, comma/newline, or paste),
  // trimming, de-duping, and dropping blanks.
  const addConvoIds = (raw) => {
    const incoming = String(raw)
      .split(/[\s,]+/)
      .map(s => s.trim())
      .filter(Boolean);
    if (incoming.length === 0) return;
    setConvoIds(prev => {
      const seen = new Set(prev);
      const merged = [...prev];
      for (const id of incoming) {
        if (!seen.has(id)) {
          seen.add(id);
          merged.push(id);
        }
      }
      return merged;
    });
  };

  const removeConvoId = (id) => {
    setConvoIds(prev => prev.filter(c => c !== id));
  };

  const handleConvoKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addConvoIds(convoId);
      setConvoId('');
    } else if (e.key === 'Backspace' && convoId === '' && convoIds.length > 0) {
      // Quick remove of the last chip when the input is empty
      removeConvoId(convoIds[convoIds.length - 1]);
    }
  };

  const handleConvoPaste = (e) => {
    const text = e.clipboardData.getData('text');
    if (/[\s,]/.test(text)) {
      e.preventDefault();
      addConvoIds(text);
      setConvoId('');
    }
  };

  // Submit all conversation IDs as one job, then go to the live progress page.
  const handleProcessSingleCalls = async () => {
    // Fold any unconfirmed text in the input into the list first
    const pending = convoId.trim();
    const ids = pending
      ? Array.from(new Set([...convoIds, ...pending.split(/[\s,]+/).map(s => s.trim()).filter(Boolean)]))
      : convoIds;

    if (!selectedCampaign || !selectedScorecard) {
      return setAlert({
        variant: 'error',
        heading: 'Empty Parameter',
        text: 'Make sure a Queue/Scorecard is selected.'
      });
    }
    if (ids.length === 0) {
      return setAlert({
        variant: 'error',
        heading: 'Empty Parameter',
        text: 'Add at least one valid Conversation ID.'
      });
    }

    setIsProcessing(true);
    try {
      // Underscore-free job ID (the pipeline parses jobId from the _JOB_<id>_ filename token)
      const readableDate = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const jobId = `single-${readableDate}`;

      const res = await singleGenesysUpload(
        selectedCampaign.value,
        selectedScorecard.value,
        ids,
        jobId
      );

      const skipped = (res && res.skipped) || [];
      setAlert({
        variant: skipped.length > 0 ? 'warning' : 'success',
        heading: 'Job Submitted',
        text: skipped.length > 0
          ? `Submitted ${res.conversations} conversation(s) (${res.recordings} recording(s)). Skipped ${skipped.length} with no recordings: ${skipped.join(', ')}.`
          : `Submitted ${res.conversations} conversation(s) (${res.recordings} recording(s)) for processing.`
      });

      setConvoId('');
      setConvoIds([]);
      setTimeout(() => navigate(`/jobs/${jobId}`), 1200);
    } catch (error) {
      console.error('Single upload error:', error);
      setAlert({
        variant: 'error',
        heading: 'Upload Failed',
        text: error.message || 'An unknown error occurred during the upload process.'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const clearAgents = () => {
      updateFilter('userIds', []);
      setAgentSearch(''); // Clear the text input as well
  };

  const filteredQueues = queueOptions.filter(q => 
    q.name.toLowerCase().includes(queueSearch.toLowerCase())
  );

  // Clear all files
  const clearFiles = () => {
    setFiles([]);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loadingCampaigns && campaigns.length === 0 && !campaignError) {
    return (
      <LoadingState variant="form" label="Loading campaigns" />
    );
  }

  return (
    <div className="space-y-6">
      {/* Guidelines - Conditional based on mode */}
      {uploadMode === 'file' ? (
        <InfoCard
          id="upload-file"
          title="Upload Guidelines"
          items={[
            'Ensure audio files are clear and have good quality',
            'Files will be automatically transcribed and analyzed',
            'Processing time depends on file size and duration',
            'Use descriptive file names for easier identification',
            'File names must be 200 characters or less',
            'Supported formats: WAV, MP3, M4A, FLAC',
          ]}
        />
      ) : (
        <InfoCard
          id="upload-genesys"
          title="Genesys Fetch Guidelines"
          items={[
            'Date range is required (max 7 days, defaults to last 7 days)',
            'Filters are optional - use them to narrow down your search',
            'Queue IDs, User IDs, Wrap-Up Codes, and Division IDs support multiple values',
            'Hard-coded filters always applied: Voice calls only, Answered calls only',
            'ANI/DNIS filters must use format: tel:+[number]',
            'Recordings are fetched asynchronously - check Jobs page for progress',
            'Queue and Scorecard selection are required',
          ]}
        />
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Upload Recordings</h1>
          <p className="text-gray-600 mt-1">
            Upload audio files for quality analysis and transcription
          </p>
        </div>

        <button
          onClick={() => navigate('/fileCopy')}
          className="btn-outline"
        >
          <FaCloud />
          Cloud Connector
        </button>
      </div>

      {/* Mode Toggle — full-width segmented control; each tab takes an equal
          share so the pill spans the page instead of huddling at the left. */}
      <div className="flex w-full items-center rounded-full border border-gray-200 p-0.5 bg-white shadow-sm" data-tour="upload-tabs">
        <button
          onClick={() => setUploadMode('file')}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-3.5 py-2 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors ${
            uploadMode === 'file'
              ? 'bg-cloudza-600 text-white'
              : 'text-gray-500 hover:text-gray-800'
          }`}
        >
          <FaUpload aria-hidden="true" className="flex-shrink-0" />
          <span className="sm:hidden">Upload</span>
          <span className="hidden sm:inline">File Upload</span>
        </button>
        <button
          onClick={() => setUploadMode('genesys')}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-3.5 py-2 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors ${
            uploadMode === 'genesys'
              ? 'bg-cloudza-600 text-white'
              : 'text-gray-500 hover:text-gray-800'
          }`}
        >
          <FaFilter aria-hidden="true" className="flex-shrink-0" />
          <span className="sm:hidden">Genesys</span>
          <span className="hidden sm:inline">Fetch from Genesys</span>
        </button>
        <button
          onClick={() => setUploadMode('genesysSingle')}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-3.5 py-2 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors ${
            uploadMode === 'genesysSingle'
              ? 'bg-cloudza-600 text-white'
              : 'text-gray-500 hover:text-gray-800'
          }`}
        >
          <FaFilter aria-hidden="true" className="flex-shrink-0" />
          <span className="sm:hidden">Single</span>
          <span className="hidden sm:inline">Fetch from Genesys (Single)</span>
        </button>
      </div>

      {/* Campaign Selector */}
      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-1.5">
          Choose Campaign
        </label>
        {loadingCampaigns ? (
          <p className="text-sm text-gray-500">Loading campaigns...</p>
        ) : campaignError ? (
          <p className="text-sm text-red-600">{campaignError}</p>
        ) : (
          <SelectField
            value={selectedCampaign?.value || ''}
            onChange={(e) => {
              const value = e.target.value;
              const campaign = campaigns.find((c) => c.value === value);
              setSelectedCampaign(campaign);
            }}
            className="select-field w-full"
          >
            <option value="" disabled>
              Select a campaign
            </option>
            {campaigns.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </SelectField>
        )}
      </div>

      {/* Scorecard Selector */}
      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-1.5">
          Choose Scorecard
        </label>
        {loadingScorecards ? (
          <p className="text-sm text-gray-500">Loading scorecards...</p>
        ) : scorecardError ? (
          <p className="text-sm text-red-600">{scorecardError}</p>
        ) : (
          <SelectField
            value={selectedScorecard?.value || ''}
            onChange={(e) => {
              const value = e.target.value;
              const sc = scorecards.find((s) => s.value === value);
              setSelectedScorecard(sc);
            }}
            className="select-field w-full"
          >
            <option value="" disabled>
              Select a scorecard
            </option>
            {scorecards.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </SelectField>
        )}
      </div>

      {/* Genesys Fetch Fields - Only show in genesys mode */}
      {uploadMode === 'genesys' && (
        <div className="card space-y-4">
          {/* Date Range */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3">Date Range (Required)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Start Date
                </label>
                <DateTimeInput
                  mode="datetime"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cloudza-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Defaults to 7 days ago if not specified
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  End Date
                </label>
                <DateTimeInput
                  mode="datetime"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cloudza-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Defaults to now if not specified
                </p>
              </div>
            </div>
            <div className="bg-cloudza-50 rounded-xl p-2 mt-2">
              <p className="text-xs text-cloudza-700">
                <strong>Date Range:</strong> {startDate && endDate ? (
                  (() => {
                    const days = ((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)).toFixed(1);
                    return `${days} days (max 7 days allowed)`;
                  })()
                ) : (
                  'Defaults to last 7 days. Max range: 7 days.'
                )}
              </p>
            </div>
          </div>

          {/* Per-Agent Sampling Section */}
          <div className="border-t border-gray-100 pt-4">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Per-Agent Sampling (Optional)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Calls per agent
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={callsPerAgent}
                  onChange={(e) => setCallsPerAgent(e.target.value)}
                  placeholder="Leave blank to QA all calls"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cloudza-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  QA only this many calls per agent, sampled evenly across time. Blank = all calls.
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Sample scope
                </label>
                <SelectField
                  value={samplingScope}
                  onChange={(e) => setSamplingScope(e.target.value)}
                  disabled={!callsPerAgent}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cloudza-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <option value="range">Across whole date range</option>
                  <option value="day">Per day</option>
                </SelectField>
                <p className="mt-1 text-xs text-gray-500">
                  {samplingScope === 'day'
                    ? 'N calls per agent for each day in the range.'
                    : 'N calls per agent over the entire date range.'}
                </p>
              </div>
            </div>
          </div>

          {/* Filters Section */}
          <div className="border-t border-gray-100 pt-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h3 className="text-sm font-bold text-gray-900">Filters (Optional)</h3>
              <button
                onClick={clearAllFilters}
                className="btn-outline !py-1.5 !px-3.5 !text-xs"
              >
                <FaTimes aria-hidden="true" />
                Clear All Filters
              </button>
            </div>

            <div className="space-y-4">
              {/* Direction Filter */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Call Direction
                </label>
                <SelectField
                  value={filters.direction}
                  onChange={(e) => updateFilter('direction', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cloudza-500 focus:border-transparent"
                >
                  <option value="">All Directions</option>
                  <option value="inbound">Inbound Only</option>
                  <option value="outbound">Outbound Only</option>
                </SelectField>
              </div>

              {/* 1. GENESYS QUEUE SEARCH */}
              <div className="relative">
                <div className="flex justify-between items-center mb-1.5">
                  <label className="block text-sm font-semibold text-gray-800">Genesys Queues</label>
                  {filters.queueIds.length > 0 && (
                    <button onClick={() => updateFilter('queueIds', [])} className="text-xs font-semibold text-red-600 hover:text-red-700">Clear All</button>
                  )}
                </div>
                {genesysFilterErrors.queues && (
                  <p className="text-xs text-red-600 mb-1">Live queues unavailable: {genesysFilterErrors.queues}</p>
                )}
                <input
                  type="text"
                  className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-cloudza-500 outline-none"
                  placeholder={fetchingFilters ? "Loading..." : "Search Queues..."}
                  value={queueSearch}
                  onChange={(e) => { setQueueSearch(e.target.value); setIsQueueOpen(true); }}
                  onFocus={() => setIsQueueOpen(true)}
                />
                {isQueueOpen && queueSearch && (
                  <div className="absolute z-50 w-full bg-white border border-gray-200 mt-1 rounded-lg shadow-xl max-h-40 overflow-y-auto dropdown-in">
                    {queueOptions.filter(q => q.name.toLowerCase().includes(queueSearch.toLowerCase())).map(queue => (
                      <div key={queue.id} className="p-2 hover:bg-cloudza-50/40 cursor-pointer text-sm"
                        onClick={() => {
                          if (!filters.queueIds.includes(queue.id)) updateFilter('queueIds', [...filters.queueIds, queue.id]);
                          setQueueSearch(''); setIsQueueOpen(false);
                        }}>
                        {queue.name}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {filters.queueIds.map((id, i) => (
                    <span key={id} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-cloudza-50 text-cloudza-700 border border-cloudza-100">
                      {queueOptions.find(q => q.id === id)?.name || id}
                      <button
                        onClick={() => removeFromArrayFilter('queueIds', i)}
                        className="ml-1.5 opacity-60 hover:opacity-100"
                        aria-label={`Remove queue ${queueOptions.find(q => q.id === id)?.name || id}`}
                      >
                        <FaTimes />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* 2. AGENT SEARCH (Uses isAgentOpen) */}
              <div className="relative mt-4">
                <div className="flex justify-between items-center mb-1.5">
                  <label className="block text-sm font-semibold text-gray-800">Agents</label>
                  {filters.userIds.length > 0 && (
                    <button onClick={() => { updateFilter('userIds', []); setAgentSearch(''); }} className="text-xs font-semibold text-red-600 hover:text-red-700">Clear All</button>
                  )}
                </div>
                {genesysFilterErrors.agents && (
                  <p className="text-xs text-red-600 mb-1">Live agents unavailable: {genesysFilterErrors.agents}</p>
                )}
                <input
                  type="text"
                  className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-cloudza-500 outline-none"
                  placeholder="Search Agents..."
                  value={agentSearch}
                  onChange={(e) => { setAgentSearch(e.target.value); setIsAgentOpen(true); }}
                  onFocus={() => setIsAgentOpen(true)}
                />
                {isAgentOpen && agentSearch && (
                  <div className="absolute z-50 w-full bg-white border border-gray-200 mt-1 rounded-lg shadow-xl max-h-40 overflow-y-auto dropdown-in">
                    {agentOptions.filter(a => a.name.toLowerCase().includes(agentSearch.toLowerCase())).map(agent => (
                      <div key={agent.id} className="p-2 hover:bg-cloudza-50/40 cursor-pointer text-sm"
                        onClick={() => {
                          if (!filters.userIds.includes(agent.id)) updateFilter('userIds', [...filters.userIds, agent.id]);
                          setAgentSearch(''); setIsAgentOpen(false);
                        }}>
                        {agent.name}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {filters.userIds.map((id, i) => (
                    <span key={id} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700">
                      {agentOptions.find(a => a.id === id)?.name || id}
                      <button
                        onClick={() => removeFromArrayFilter('userIds', i)}
                        className="ml-1.5 opacity-60 hover:opacity-100"
                        aria-label={`Remove agent ${agentOptions.find(a => a.id === id)?.name || id}`}
                      >
                        <FaTimes />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* 3. WRAP-UP CODE SEARCH (Uses isWrapupOpen) */}
              <div className="relative mt-4">
                <div className="flex justify-between items-center mb-1.5">
                  <label className="block text-sm font-semibold text-gray-800">Wrap-Up Codes</label>
                  {filters.wrapUpCodes.length > 0 && (
                    <button onClick={() => { updateFilter('wrapUpCodes', []); setWrapupSearch(''); }} className="text-xs font-semibold text-red-600 hover:text-red-700">Clear All</button>
                  )}
                </div>
                {genesysFilterErrors.wrapUpCodes && (
                  <p className="text-xs text-red-600 mb-1">Live wrap-up codes unavailable: {genesysFilterErrors.wrapUpCodes}</p>
                )}
                <input
                  type="text"
                  className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-cloudza-500 outline-none"
                  placeholder="Search Codes..."
                  value={wrapupSearch}
                  onChange={(e) => { setWrapupSearch(e.target.value); setIsWrapupOpen(true); }}
                  onFocus={() => setIsWrapupOpen(true)}
                />
                {isWrapupOpen && wrapupSearch && (
                  <div className="absolute z-50 w-full bg-white border border-gray-200 mt-1 rounded-lg shadow-xl max-h-40 overflow-y-auto dropdown-in">
                    {wrapUpCodeOptions.filter(c => c.name.toLowerCase().includes(wrapupSearch.toLowerCase())).map(code => (
                      <div key={code.id} className="p-2 hover:bg-cloudza-50/40 cursor-pointer text-sm"
                        onClick={() => {
                          if (!filters.wrapUpCodes.includes(code.id)) updateFilter('wrapUpCodes', [...filters.wrapUpCodes, code.id]);
                          setWrapupSearch(''); setIsWrapupOpen(false);
                        }}>
                        {code.name}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {filters.wrapUpCodes.map((id, i) => (
                    <span key={id} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
                      {wrapUpCodeOptions.find(c => c.id === id)?.name || id}
                      <button
                        onClick={() => removeFromArrayFilter('wrapUpCodes', i)}
                        className="ml-1.5 opacity-60 hover:opacity-100"
                        aria-label={`Remove wrap-up code ${wrapUpCodeOptions.find(c => c.id === id)?.name || id}`}
                      >
                        <FaTimes />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* ANI and DNIS Filters */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                    ANI (Caller Number)
                  </label>
                  <input
                    type="text"
                    value={filters.ani}
                    onChange={(e) => updateFilter('ani', e.target.value)}
                    placeholder="tel:+1234567890"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cloudza-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">Format: tel:+[number]</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                    DNIS (Dialed Number)
                  </label>
                  <input
                    type="text"
                    value={filters.dnis}
                    onChange={(e) => updateFilter('dnis', e.target.value)}
                    placeholder="tel:+0987654321"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cloudza-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">Format: tel:+[number]</p>
                </div>
              </div>

              {/* Division IDs Filter */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Division IDs
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={divisionIdInput}
                    onChange={(e) => setDivisionIdInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addToArrayFilter('divisionIds', divisionIdInput, setDivisionIdInput)}
                    placeholder="Enter division ID and press Enter"
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cloudza-500 focus:border-transparent"
                  />
                  <button
                    onClick={() => addToArrayFilter('divisionIds', divisionIdInput, setDivisionIdInput)}
                    className="btn-primary whitespace-nowrap"
                  >
                    <FaPlus />
                    Add
                  </button>
                </div>
                {filters.divisionIds.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {filters.divisionIds.map((id, index) => (
                      <span key={index} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        {id}
                        <button
                          onClick={() => removeFromArrayFilter('divisionIds', index)}
                          className="ml-1.5 opacity-60 hover:opacity-100"
                          aria-label={`Remove division ${id}`}
                        >
                          <FaTimes />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Fetch Button - Shows Preview First */}
          <button
            onClick={handleShowPreview}
            disabled={
              fetchingCalls ||
              !selectedCampaign ||
              !selectedScorecard ||
              previewLoading
            }
            className="btn-primary disabled:opacity-50"
          >
            {fetchingCalls ? (
              <>
                <InlineSpinner light className="mr-2" />
                Fetching Calls...
              </>
            ) : (
              <>
                <FaEye aria-hidden="true" />
                Preview & Fetch Calls from Genesys
              </>
            )}
          </button>
        </div>
      )}

      {uploadMode === 'genesysSingle' && (
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1.5">
            Conversation IDs
          </label>

          {/* Chip / tag input — type + Enter (or comma) to add, or paste a comma/newline-separated list */}
          <div className="flex flex-wrap items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg focus-within:ring-2 focus-within:ring-cloudza-500 focus-within:border-transparent">
            {convoIds.map((id) => (
              <span
                key={id}
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-cloudza-50 text-cloudza-700 border border-cloudza-100"
              >
                {id}
                <button
                  type="button"
                  onClick={() => removeConvoId(id)}
                  className="ml-1.5 opacity-60 hover:opacity-100 focus:outline-none"
                  aria-label={`Remove ${id}`}
                >
                  <FaTimes />
                </button>
              </span>
            ))}
            <input
              type="text"
              value={convoId}
              onChange={(e) => setConvoId(e.target.value)}
              onKeyDown={handleConvoKeyDown}
              onPaste={handleConvoPaste}
              onBlur={() => { if (convoId.trim()) { addConvoIds(convoId); setConvoId(''); } }}
              placeholder={convoIds.length === 0 ? 'Enter a Conversation ID and press Enter (or paste a list)' : 'Add another…'}
              className="flex-1 min-w-[12rem] px-1 py-1 border-0 focus:ring-0 focus:outline-none text-sm"
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
            <p className="text-sm text-gray-500">
              {convoIds.length} conversation{convoIds.length !== 1 ? 's' : ''} added
            </p>
            <button
              disabled={isProcessing}
              onClick={handleProcessSingleCalls}
              className="btn-primary whitespace-nowrap disabled:opacity-50"
            >
              <FaPlay />
              {isProcessing ? 'Submitting...' : 'Process Calls'}
            </button>
          </div>
        </div>
      )}

      {/* Upload Area - Only show in file mode */}
      {uploadMode === 'file' && (
        <div
          data-tour="upload-dropzone"
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
            dragActive
              ? 'border-cloudza-400 bg-cloudza-50'
              : 'border-gray-200 hover:border-cloudza-300'
          }`}
          onDrop={handleDrop}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
        >
        <div className="mx-auto w-12 h-12 rounded-2xl bg-cloudza-50 text-cloudza-700 flex items-center justify-center">
          <FaCloudUploadAlt className="text-2xl" aria-hidden="true" />
        </div>

        <div className="mt-4">
          <p className="text-lg font-medium text-gray-900">
            Upload audio files
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Supports WAV, MP3, M4A, FLAC files up to 100MB each
          </p>
        </div>

        <div className="mt-6">
          <label className="cursor-pointer">
            <input
              type="file"
              multiple
              accept="audio/*,.wav,.mp3,.m4a,.flac"
              onChange={handleFileChange}
              className="hidden"
            />
            <span className="btn-primary">
              <FaUpload aria-hidden="true" />
              Select Files
            </span>
          </label>
          <p className="text-xs text-gray-400 mt-2">or drag and drop here</p>
        </div>
      </div>
      )}

      {/* File List - Only show in file mode */}
      {uploadMode === 'file' && files.length > 0 && (
        <div className="card">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2.5">
              <h3 className="text-sm font-bold text-gray-900">Files to Upload</h3>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-cloudza-50 text-cloudza-700 text-xs font-semibold">
                {files.length} {files.length === 1 ? 'file' : 'files'}
              </span>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={clearFiles}
                disabled={uploading}
                className="btn-outline disabled:opacity-50"
              >
                <FaTimes />
                Clear All
              </button>
              <button
                onClick={handleUpload}
                disabled={
                  uploading ||
                  files.length === 0 ||
                  !selectedCampaign ||
                  !selectedScorecard
                }
                className="btn-primary disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <InlineSpinner light className="mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FaUpload aria-hidden="true" />
                    Upload All
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {files.map((fileData) => (
              <FileItem
                key={fileData.id}
                fileData={fileData}
                onRemove={() => removeFile(fileData.id)}
                onMetaChange={(field, value) => updateFileMeta(fileData.id, field, value)}
                formatFileSize={formatFileSize}
              />
            ))}
          </div>
        </div>
      )}

      {/* Genesys Preview Modal */}
      <GenesysPreviewModal
        open={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewData(null);
          setPreviewError(null);
        }}
        onConfirm={() => handleFetchInteractionCalls(previewData)}
        previewData={previewData}
        loading={previewLoading}
        error={previewError}
      />
    </div>
  );
};

// File Item Component
const FileItem = ({ fileData, onRemove, onMetaChange, formatFileSize }) => {
  const meta = fileData.meta || {};
  const anyDetected = meta.agentDetected || meta.guidDetected || meta.datetimeDetected;
  const [showMeta, setShowMeta] = useState(!!anyDetected);

  const getStatusIcon = () => {
    switch (fileData.status) {
      case 'pending':
        return <FaRegClock className="w-5 h-5 text-gray-400" aria-hidden="true" />;
      case 'uploading':
        return <InlineSpinner className="!h-5 !w-5" />;
      case 'completed':
        return <FaCheckCircle className="w-5 h-5 text-green-500" aria-hidden="true" />;
      case 'error':
        return <FaTimesCircle className="w-5 h-5 text-red-500" aria-hidden="true" />;
      default:
        return null;
    }
  };

  const getStatusChip = () => {
    const base = 'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium';
    switch (fileData.status) {
      case 'pending':
        return <span className={`${base} bg-gray-100 text-gray-600`}>Pending</span>;
      case 'uploading':
        return <span className={`${base} bg-cloudza-50 text-cloudza-700`}>Uploading… {fileData.progress}%</span>;
      case 'completed':
        return <span className={`${base} bg-green-50 text-green-700`}>Completed</span>;
      case 'error':
        return <span className={`${base} bg-red-50 text-red-700`}>Failed</span>;
      default:
        return <span className={`${base} bg-gray-100 text-gray-600`}>Unknown</span>;
    }
  };

  // Compact badge shown after upload completes
  const metaBadge = () => {
    const parts = [];
    if (meta.agent)    parts.push(`Agent: ${meta.agent}`);
    if (meta.datetime) parts.push(meta.datetime.replace('T', ' '));
    if (meta.guid)     parts.push(`GUID: ${meta.guid}`);
    return parts.length > 0 ? parts.join(' · ') : null;
  };

  const isPending = fileData.status === 'pending';

  return (
    <div className="p-3 bg-gray-50 rounded-lg">
      {/* Main row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div className="flex-shrink-0">{getStatusIcon()}</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{fileData.file.name}</p>
            <div className="flex flex-wrap items-center gap-2 mt-0.5">
              <span className="text-xs text-gray-500 tabular-nums">{formatFileSize(fileData.file.size)}</span>
              {getStatusChip()}
            </div>
            {!isPending && metaBadge() && (
              <p className="text-xs text-cloudza-700 mt-0.5">{metaBadge()}</p>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
          {fileData.status === 'uploading' && (
            <div className="w-24">
              <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
                <div
                  className="h-full bg-cloudza-600 rounded-full transition-all duration-300"
                  style={{ width: `${fileData.progress}%` }}
                />
              </div>
            </div>
          )}

          {isPending && (
            <button
              onClick={() => setShowMeta(v => !v)}
              className="btn-outline !py-1.5 !px-3.5 !text-xs whitespace-nowrap"
            >
              {showMeta ? <FaMinus aria-hidden="true" /> : <FaPlus aria-hidden="true" />}
              {showMeta ? 'Metadata' : 'Add Metadata'}
            </button>
          )}

          {fileData.status !== 'uploading' && (
            <button
              onClick={onRemove}
              className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              title="Remove file"
              aria-label={`Remove ${fileData.file.name}`}
            >
              <FaTimes className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* Metadata sub-row — only while pending */}
      {isPending && showMeta && (
        <div className="mt-3 grid grid-cols-3 gap-3 pl-8">
          {/* Agent */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Agent
              {meta.agentDetected && (
                <span className="ml-1 inline-flex items-center gap-1 text-green-600"><FaCheck className="text-[10px]" aria-hidden="true" /> in filename</span>
              )}
            </label>
            <input
              type="text"
              value={meta.agent || ''}
              disabled={meta.agentDetected}
              onChange={e => onMetaChange('agent', e.target.value)}
              placeholder="e.g. BobS"
              className="w-full px-2 py-1 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-cloudza-500 focus:border-cloudza-500 disabled:bg-gray-100 disabled:text-gray-500"
            />
            <p className="text-xs text-gray-400 mt-0.5">→ _AGENT_</p>
          </div>

          {/* Date & Time */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Call Date & Time
              {meta.datetimeDetected && (
                <span className="ml-1 inline-flex items-center gap-1 text-green-600"><FaCheck className="text-[10px]" aria-hidden="true" /> in filename</span>
              )}
            </label>
            <DateTimeInput
              mode="datetime"
              value={meta.datetime || ''}
              disabled={meta.datetimeDetected}
              onChange={e => onMetaChange('datetime', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-cloudza-500 focus:border-cloudza-500 disabled:bg-gray-100 disabled:text-gray-500"
            />
            <p className="text-xs text-gray-400 mt-0.5">→ _DT_</p>
          </div>

          {/* GUID */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Call GUID / ID
              {meta.guidDetected && (
                <span className="ml-1 inline-flex items-center gap-1 text-green-600"><FaCheck className="text-[10px]" aria-hidden="true" /> in filename</span>
              )}
            </label>
            <input
              type="text"
              value={meta.guid || ''}
              disabled={meta.guidDetected}
              onChange={e => onMetaChange('guid', e.target.value)}
              placeholder="e.g. abc-123"
              className="w-full px-2 py-1 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-cloudza-500 focus:border-cloudza-500 disabled:bg-gray-100 disabled:text-gray-500"
            />
            <p className="text-xs text-gray-400 mt-0.5">→ _GUID_</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadRecordings;